import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import type { Device } from '../../api/types';
import { DeviceIcon } from '../common/DeviceIcon';
import { useDeviceStore } from '../../stores';

interface DeviceCardProps {
  device: Device;
  index?: number;
}

export function DeviceCard({ device, index = 0 }: DeviceCardProps) {
  const navigate = useNavigate();
  const { setDeviceValue } = useDeviceStore();

  const powerParam = device.parameters.find(p => 
    p.name === 'power' || p.name === 'power_1'
  );
  const isOn = powerParam?.boolValue ?? false;
  const brightnessParam = device.parameters.find(p => p.name.includes('brightness'));
  const temperatureParam = device.parameters.find(p => p.name.includes('temperature'));
  const humidityParam = device.parameters.find(p => p.name.includes('humidity'));

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!powerParam || !device.isOnline) return;
    
    await setDeviceValue(device.deviceId, {
      ...powerParam,
      boolValue: !isOn
    });
  };

  const getStatusText = () => {
    if (!device.isOnline) return 'Оффлайн';
    if (brightnessParam) return `${brightnessParam.numericValue}%`;
    if (temperatureParam) return `${temperatureParam.numericValue}°`;
    if (humidityParam) return `${humidityParam.numericValue}%`;
    if (powerParam) return isOn ? 'Вкл' : 'Выкл';
    return device.parameters[0]?.rusValueOnlyView || '—';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      onClick={() => navigate(`/devices/${device.deviceId}`)}
      className="glass-card-hover p-5 cursor-pointer group"
    >
      <div className="flex items-center gap-4">
        <div 
          onClick={handleToggle}
          className={`
            relative w-14 h-14 rounded-2xl flex items-center justify-center
            transition-all duration-300 cursor-pointer
            ${device.isOnline && isOn 
              ? 'gradient-primary shadow-lg shadow-indigo-500/30' 
              : 'bg-slate-100/80'
            }
            ${!device.isOnline && 'opacity-50'}
          `}
        >
          <DeviceIcon 
            type={device.type} 
            size="md" 
            className={`${device.isOnline && isOn ? 'text-white' : 'text-slate-400'}`}
          />
          
          {device.isOnline && isOn && (
            <motion.div
              layoutId={`glow-${device.id}`}
              className="absolute inset-0 rounded-2xl gradient-primary opacity-0 group-hover:opacity-20 blur-xl transition-opacity"
            />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800 text-base truncate tracking-tight">
            {device.customName || device.baseName}
          </h3>
          <p className="text-sm text-slate-400 mt-0.5">{device.room}</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className={`
              text-sm font-medium tracking-tight
              ${!device.isOnline ? 'text-slate-300' : 
                isOn || brightnessParam || temperatureParam ? 'text-slate-700' : 'text-slate-400'}
            `}>
              {getStatusText()}
            </p>
            {!device.isOnline && (
              <div className="w-2 h-2 rounded-full bg-slate-200 mx-auto mt-1" />
            )}
            {device.isOnline && isOn && (
              <div className="w-2 h-2 rounded-full gradient-success mx-auto mt-1 glow-sm" />
            )}
          </div>
          
          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-400 transition-colors" />
        </div>
      </div>
    </motion.div>
  );
}
